import React from "react";
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { OrganizationType } from "../../store/apis/clientRegistryApi";

// El orden del desplegable, que no es el del enum: primero lo que más se elige.
export const ORGANIZATION_TYPES: OrganizationType[] = [
  "ANUNCIANTE",
  "AGENCIA",
  "MEDIO",
  "PARTNER_TECNOLOGICO",
  "PROVEEDOR",
  "CONSULTORA",
  "INSTITUCION",
  "ASOCIACION",
  "OTRO",
];

export const useOrganizationTypeLabel = () => {
  const { t } = useTranslation();
  const fallback: Record<string, string> = {
    ANUNCIANTE: "Anunciante",
    AGENCIA: "Agencia",
    MEDIO: "Medio",
    PARTNER_TECNOLOGICO: "Partner tecnológico",
    PROVEEDOR: "Proveedor",
    CONSULTORA: "Consultora",
    INSTITUCION: "Institución",
    ASOCIACION: "Asociación",
    OTRO: "Otro",
  };
  return (type: OrganizationType) =>
    t(`company.organizationType.${type}`, fallback[type] ?? type);
};

interface Props {
  value: OrganizationType[];
  onChange: (next: OrganizationType[]) => void;
  disabled?: boolean;
}

/**
 * Qué es la empresa: agencia, anunciante, medio…
 *
 * Es multi-select y no una sola opción porque una empresa puede ser varias cosas a la
 * vez: Havas es la agencia de unos clientes y cliente nuestro por su cuenta. Con un
 * único valor habría que elegir cuál de las dos verdades se guarda.
 *
 * No es obligatorio: las 2.400 empresas que ya existen entran sin ningún tipo y se van
 * etiquetando cuando alguien pase por su ficha.
 */
const OrganizationTypesField: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const label = useOrganizationTypeLabel();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        sx={{
          fontFamily: "Nunito",
          fontWeight: 700,
          color: theme.palette.grey[900],
        }}
      >
        {t("company.organizationTypes", "Tipo de organización")}
      </Typography>
      <FormControl fullWidth>
        <Select
          multiple
          displayEmpty
          disabled={disabled}
          value={value}
          onChange={(event) =>
            onChange(event.target.value as OrganizationType[])
          }
          renderValue={(selected) =>
            selected.length === 0 ? (
              <Typography
                sx={{
                  fontFamily: "Nunito",
                  fontSize: 16,
                  color: theme.palette.neutral[500],
                }}
              >
                {t("company.organizationTypesEmpty", "Sin clasificar")}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((type) => (
                  <Chip key={type} size="small" label={label(type)} />
                ))}
              </Box>
            )
          }
          sx={{
            borderRadius: 50,
            fontFamily: "Nunito",
            fontSize: 16,
            "& fieldset": { borderColor: theme.palette.neutral[400] },
          }}
        >
          {ORGANIZATION_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {label(type)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default OrganizationTypesField;
